import React from "react";
import PropTypes from "prop-types";
import ModernModal from "components/ModernModal";

const F = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parseFloat(v) || 0);

export default function WithdrawalGate({
  isOpen,
  onClose,
  pendingPayment,
  refundableBalance,
  onPayNow,
  onProceedToWithdraw,
}) {
  const hasPending = parseFloat(pendingPayment) > 0;

  return (
    <ModernModal
      isOpen={isOpen}
      onClose={onClose}
      title={hasPending ? "Claim Refunds" : "Withdraw Funds"}
      size="md"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        {/* Pending alert */}
        {hasPending ? (
          <>
            <div style={{
              background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(249,115,22,0.04))",
              border: "1px solid rgba(245,158,11,0.2)",
              borderRadius: "var(--radius-lg)", padding: "1.25rem",
              backdropFilter: "blur(8px)", display: "flex", alignItems: "flex-start", gap: "0.75rem",
            }}>
              <span style={{ fontSize: "1.5rem", flexShrink: 0 }}>⚠️</span>
              <div>
                <h4 style={{ margin: "0 0 4px 0", fontSize: "0.9rem", color: "var(--text-primary)" }}>Outstanding Balance</h4>
                <p style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "var(--warning)" }}>
                  {F(pendingPayment)}
                </p>
              </div>
            </div>

            {/* Glass info card */}
            <div style={{
              background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "var(--radius-lg)", padding: "1.25rem", backdropFilter: "blur(10px)",
              display: "flex", flexDirection: "column", gap: "0.85rem",
            }}>
              <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                <strong style={{ color: "var(--text-primary)" }}>Clear your outstanding balance</strong> of <strong>{F(pendingPayment)}</strong> to unlock your refund of <strong>{F(refundableBalance)}</strong>.
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", lineHeight: 1.5 }}>
                Payment methods: Cryptocurrency, P2P, or Gift Cards.
              </div>
            </div>

            {/* Balance summary */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px",
            }}>
              <div style={{ padding: "0.75rem", borderRadius: "var(--radius-sm)", background: "var(--danger-bg)", textAlign: "center" }}>
                <div style={{ fontSize: "0.68rem", textTransform: "uppercase", color: "var(--danger)", marginBottom: 4, fontWeight: 600 }}>Owed</div>
                <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--danger)" }}>{F(pendingPayment)}</div>
              </div>
              <div style={{ padding: "0.75rem", borderRadius: "var(--radius-sm)", background: "var(--success-bg)", textAlign: "center" }}>
                <div style={{ fontSize: "0.68rem", textTransform: "uppercase", color: "var(--success)", marginBottom: 4, fontWeight: 600 }}>Refundable</div>
                <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--success)" }}>{F(refundableBalance)}</div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={onPayNow} style={{
                flex: 1, padding: "0.75rem", border: "none", borderRadius: "var(--radius-sm)",
                background: "var(--gradient-card)", color: "#fff", fontWeight: 600, cursor: "pointer", fontSize: "0.88rem",
              }}>Pay Outstanding Balance</button>
              <button onClick={onClose} style={{
                padding: "0.75rem 1.25rem", border: "2px solid var(--accent)", borderRadius: "var(--radius-sm)",
                background: "transparent", color: "var(--accent)", fontWeight: 600, cursor: "pointer", fontSize: "0.88rem",
              }}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            {/* No pending balance */}
            <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem", padding: "1rem 0" }}>
              <span style={{ fontSize: "3rem" }}>✅</span>
              <h4 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem" }}>No Outstanding Balance</h4>
              <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: "0.85rem" }}>
                Your account is in good standing.
              </p>
              <div style={{
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "var(--radius-lg)", padding: "1rem 1.5rem", backdropFilter: "blur(10px)",
                width: "100%",
              }}>
                <div style={{ fontSize: "0.7rem", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 4 }}>
                  Available for Withdrawal
                </div>
                <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--success)" }}>
                  {F(refundableBalance)}
                </div>
              </div>
              <button onClick={() => { onClose(); onProceedToWithdraw(); }} style={{
                width: "100%", padding: "0.8rem", border: "none", borderRadius: "var(--radius-sm)",
                background: "var(--gradient-card)", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: "0.92rem",
                marginTop: "0.5rem",
              }}>Proceed to Withdrawal</button>
            </div>
          </>
        )}

        {/* Footer */}
        <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", textAlign: "center", margin: 0 }}>
          Need help? Contact <a href="/support" style={{ color: "var(--accent)" }}>support</a>.
        </p>
      </div>
    </ModernModal>
  );
}

WithdrawalGate.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  pendingPayment: PropTypes.string,
  refundableBalance: PropTypes.string,
  onPayNow: PropTypes.func.isRequired,
  onProceedToWithdraw: PropTypes.func.isRequired,
};